import type { Account } from '../types';

// The default chart parents cash/bank leaf accounts under the "1001 Cash & Bank"
// header, in the 1010–1099 asset range, tagging real bank accounts `bank-feed`
// (bank_account_service.py: BANK_PARENT_HEADER '1001', BANK_ACCOUNT_NUMBER_RANGE
// (1010, 1099)). Petty cash sits in the same subtree without the tag.
const CASH_BANK_HEADER_NUMBER = '1001';
const CASH_BANK_RANGE_START = 1000;
const CASH_BANK_RANGE_END = 1099;
const BANK_FEED_TAG = 'bank-feed';

/**
 * Deposit-eligible accounts for a receipt/payment: postable, active asset
 * accounts that are cash or bank accounts — NOT the entire asset chart (which
 * would wrongly offer Accounts Receivable, Accumulated Depreciation, Furniture…).
 *
 * An account qualifies when it is a direct child of the Cash & Bank header, OR
 * carries the `bank-feed` tag, OR its number falls in the reserved 1000–1099
 * cash/bank range — the union stays correct across renumbered or custom charts.
 */
export function filterCashAndBankAccounts(accounts: Account[]): Account[] {
  const cashBankHeaderId = accounts.find(
    (a) => a.is_header && a.account_number === CASH_BANK_HEADER_NUMBER,
  )?.id;

  return accounts.filter((a) => {
    if (a.is_header || a.is_active === false || a.account_type !== 'asset') return false;
    if (cashBankHeaderId && a.parent_account_id === cashBankHeaderId) return true;
    if (a.tags?.includes(BANK_FEED_TAG)) return true;
    const num = Number(a.account_number);
    return Number.isInteger(num) && num >= CASH_BANK_RANGE_START && num <= CASH_BANK_RANGE_END;
  });
}
